import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarBlank, CaretLeft, CaretRight, Plus, FacebookLogo, InstagramLogo, TiktokLogo, Trash } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const CHANNELS = [
  { key: "facebook", label: "Facebook", icon: FacebookLogo, color: "text-sky-400" },
  { key: "instagram", label: "Instagram", icon: InstagramLogo, color: "text-pink-400" },
  { key: "tiktok", label: "TikTok", icon: TiktokLogo, color: "text-fuchsia-400" },
];
const STATUS_META = {
  draft: { label: "Bozza", cls: "bg-white/5 text-white/50 border-white/15" },
  scheduled: { label: "Programmato", cls: "bg-sky-400/15 text-sky-300 border-sky-400/30" },
  published: { label: "Pubblicato", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  reminder: { label: "Da pubblicare a mano", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  failed: { label: "Fallito", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  publishing: { label: "In corso", cls: "bg-white/5 text-white/50 border-white/15" },
};
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const emptyForm = { text: "", channels: ["facebook"], scheduled_at: "", page_id: "" };

export default function MarketingCalendarPage() {
  const [posts, setPosts] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); return [d.getFullYear(), d.getMonth()]; });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/social/posts").then(({ data }) => setPosts(data.posts)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [year, mon] = month;
  const firstDay = new Date(year, mon, 1);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const postsByDay = (day) => posts.filter((p) => {
    if (!p.scheduled_at) return false;
    const d = new Date(p.scheduled_at);
    return d.getFullYear() === year && d.getMonth() === mon && d.getDate() === day;
  });

  const openNew = (day) => {
    const dt = day ? new Date(year, mon, day, 10, 0) : null;
    const pad = (n) => String(n).padStart(2, "0");
    setForm({ ...emptyForm, scheduled_at: dt ? `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T10:00` : "" });
    setEditing("new");
  };

  const openEdit = (p) => {
    let local = "";
    if (p.scheduled_at) {
      const d = new Date(p.scheduled_at);
      const pad = (n) => String(n).padStart(2, "0");
      local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setForm({ text: p.text, channels: p.channels, scheduled_at: local, page_id: p.page_id || "" });
    setEditing(p);
  };

  const toggleChannel = (key) => {
    const has = form.channels.includes(key);
    if (has && form.channels.length === 1) return;
    setForm({ ...form, channels: has ? form.channels.filter((c) => c !== key) : [...form.channels, key] });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : "" };
    try {
      const { data } = editing === "new"
        ? await api.post("/admin/social/posts", payload)
        : await api.put(`/admin/social/posts/${editing.id}`, payload);
      toast.success(data.message);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { data } = await api.delete(`/admin/social/posts/${editing.id}`);
      toast.success(data.message);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const upcoming = posts.filter((p) => ["scheduled", "reminder", "failed", "draft"].includes(p.status)).slice(0, 8);

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <Link to="/marketing/social" data-testid="back-to-social" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
            <CaretLeft size={13} /> Social
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
            <CalendarBlank size={13} className="text-fuchsia-400" /> Pianificazione contenuti
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Calendario editoriale</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Pianifica i post su Facebook, Instagram e TikTok. Con Meta collegato i post Facebook partono da soli; per gli altri canali ricevi una notifica quando è ora di pubblicare.
          </p>
        </div>
        <Button data-testid="calendar-new-post-button" onClick={() => openNew(null)}
          className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          <Plus size={15} className="mr-1.5" /> Nuovo post
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-lg bg-surface border border-surface-border animate-fade-up">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="calendar-prev-month" onClick={() => setMonth(mon === 0 ? [year - 1, 11] : [year, mon - 1])}
              className="h-8 w-8 rounded-full border border-white/15 text-white/60 hover:text-white inline-flex items-center justify-center"><CaretLeft size={14} /></button>
            <p className="font-heading text-lg font-semibold" data-testid="calendar-month-label">{MONTHS[mon]} {year}</p>
            <button data-testid="calendar-next-month" onClick={() => setMonth(mon === 11 ? [year + 1, 0] : [year, mon + 1])}
              className="h-8 w-8 rounded-full border border-white/15 text-white/60 hover:text-white inline-flex items-center justify-center"><CaretRight size={14} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-white/30 mb-1">
            {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {cells.map((day, i) => (
              <button key={i} type="button" disabled={!day} onClick={() => day && openNew(day)}
                data-testid={day ? `calendar-day-${day}` : undefined}
                className={`min-h-[64px] p-1.5 rounded-md text-left align-top border transition-colors ${day ? "border-white/5 hover:border-white/25 hover:bg-white/[0.03]" : "border-transparent"}`}>
                {day && <span className="text-[11px] text-white/45">{day}</span>}
                <div className="flex flex-wrap gap-1 mt-1">
                  {day && postsByDay(day).map((p) => (
                    <span key={p.id} title={p.text} onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      className={`w-2 h-2 rounded-full cursor-pointer ${p.status === "published" ? "bg-emerald-400" : p.status === "failed" ? "bg-red-500" : p.status === "reminder" ? "bg-amber-400" : "bg-sky-400"}`} />
                  ))}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-white/30 mt-3">Clicca un giorno per creare un post, un pallino per modificarlo. Blu = programmato, verde = pubblicato, ambra = da pubblicare a mano, rosso = fallito.</p>
        </div>

        <div className="p-6 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="calendar-upcoming">
          <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Post recenti e in coda</p>
          {upcoming.length === 0 && <p className="text-xs text-white/40">Nessun post pianificato. Crea il primo!</p>}
          <div className="space-y-2">
            {upcoming.map((p) => {
              const st = STATUS_META[p.status] || STATUS_META.draft;
              return (
                <button key={p.id} type="button" onClick={() => openEdit(p)} data-testid={`upcoming-post-${p.id}`}
                  className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/20 transition-colors">
                  <p className="text-xs text-white/80 line-clamp-2">{p.text}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {p.channels.map((c) => {
                      const ch = CHANNELS.find((x) => x.key === c);
                      const Icon = ch.icon;
                      return <Icon key={c} size={12} className={ch.color} />;
                    })}
                    <span className={`ml-auto text-[9px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                  </div>
                  {p.scheduled_at && <p className="text-[10px] text-white/35 mt-1">{new Date(p.scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</p>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="calendar-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">{editing === "new" ? "Nuovo post" : "Modifica post"}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Senza data viene salvato come bozza. Facebook parte in automatico (con Meta collegato); Instagram e TikTok ricevono un promemoria push.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Testo del post</Label>
              <Textarea required data-testid="post-text-input" value={form.text} rows={4}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Scrivi qui il contenuto..." className="bg-transparent border-white/10 text-white placeholder:text-white/25 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Canali</Label>
              <div className="flex gap-2">
                {CHANNELS.map((ch) => {
                  const Icon = ch.icon;
                  const active = form.channels.includes(ch.key);
                  return (
                    <button key={ch.key} type="button" data-testid={`post-channel-${ch.key}`} onClick={() => toggleChannel(ch.key)}
                      className={`h-9 px-4 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors ${active ? "bg-white/10 text-white border-white/40" : "border-white/15 text-white/45 hover:text-white"}`}>
                      <Icon size={14} className={active ? ch.color : ""} /> {ch.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Data e ora di pubblicazione (vuota = bozza)</Label>
              <Input type="datetime-local" data-testid="post-datetime-input" value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                className="h-10 bg-transparent border-white/10 text-white text-sm [color-scheme:dark]" />
            </div>
            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={saving} data-testid="post-save-button"
                className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm flex-1">
                {saving ? "Salvataggio..." : editing === "new" ? (form.scheduled_at ? "Programma post" : "Salva bozza") : "Salva modifiche"}
              </Button>
              {editing !== "new" && (
                <Button type="button" onClick={handleDelete} variant="ghost" data-testid="post-delete-button"
                  className="h-11 px-4 border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-full">
                  <Trash size={15} />
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
